// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const { composeRefs } = require('client-v2/common/components/utils')
const Icon = require('client-v2/common/containers/icons/component')
const usePrevious = require('client-v2/common/hooks/previous')
const {
  useModalDepthModifier,
  useModalDepthDisableTouchScrollingBehindOverlayEffect
 } = require('client-v2/common/contexts/modal-depth')
 const { keyCodesByName } = require('client-v2/common/utils/events')
 const tag = require('client-v2/common/utils/hyperscript')
// Container imports
const ArticleProfileDetailCard = require('../components/detail-card-smart')
const ArticleProfileRelated = require('../components/related-smart')
// Browser-only client V2 common imports
let mediaQueryManager
if (typeof window === 'object') {
  mediaQueryManager = require('client-v2/common/utils/media-query-manager')
}

// BEM Block entity definition
const bem = BemEntity('ArticleProfileModal')

// Swipe constants
const MAX_SWIPE_WIDTH_FACTOR = 0.9
const MIN_SWIPE_WIDTH_FACTOR = 0.25
const BROWSER_BACK_SWIPE_RANGE = 40 // px
const KEY_SCROLL_AMOUNT = 100 // px
const SwipeDirectionEnum = {
  NONE: 'none',
  LEFT: 'left',
  RIGHT: 'right'
}

// Keycode constants
const CLOSE_MODAL_KEYCODE = keyCodesByName.escape
const SWIPE_LEFT_KEYCODE = keyCodesByName.arrowLeft
const SWIPE_RIGHT_KEYCODE = keyCodesByName.arrowRight
const SCROLL_UP_KEYCODE = keyCodesByName.arrowUp
const SCROLL_DOWN_KEYCODE = keyCodesByName.arrowDown
const captureKeyCodes = [
  CLOSE_MODAL_KEYCODE,
  SWIPE_LEFT_KEYCODE,
  SWIPE_RIGHT_KEYCODE,
  SCROLL_UP_KEYCODE,
  SCROLL_DOWN_KEYCODE
]

const DEBOUNCE_DELAY = 300 // ms
const DEBOUNCE_OPTIONS = { leading: true, trailing: false }

function ArticleProfileModal(props) {
  const prevProps = usePrevious(props)
  const currentSlideRef = React.useRef(null)
  const [isSwiping, setSwiping] = React.useState(false)
  const [swipeDirection, setSwipeDirection] = React.useState(SwipeDirectionEnum.NONE)
  const [swipeWidth, setSwipeWidth] = React.useState(0)
  const [swipe, setSwipe] = React.useState(null)

  useModalDepthModifier()

  const stopBumpAnimation = useBumpAnimation({
    isNewlyVisible: !currentSlideRef,
    setOffset: setSwipeWidth
  })

  React.useEffect(() => {
    if (typeof window === 'object') {
      window.addEventListener('keydown', onKeyDown)
      document.addEventListener('touchstart', onSwipeStart)
      document.addEventListener('touchmove', onSwipeMove)
      document.addEventListener('touchend', onSwipeEnd)
    }

    if (prevProps) {
      const indexDiff = prevProps.currentIndex - props.currentIndex
      const depthDiff = prevProps.modalDepth - props.modalDepth
      const countDiff = prevProps.slideCount - props.slideCount

      if (!props.slideCount) {
        // Close the modal if there are no slides to show
        props.onClose()
      } else if (countDiff >= 1 && !depthDiff) {
        // Refresh the current slide if slides have been removed
        props.onSlideRefresh()
      } else if (Math.abs(indexDiff) > 1 || depthDiff) {
        // Prevent sliding behavior when showing related article profile modal
        setSwipeDirection(SwipeDirectionEnum.NONE)
      } else if (indexDiff > 0) {
        setSwipeDirection(SwipeDirectionEnum.LEFT)
      } else if (indexDiff < 0) {
        setSwipeDirection(SwipeDirectionEnum.RIGHT)
      }
    }

    return () => {
      if (typeof window === 'object') {
        window.removeEventListener('keydown', onKeyDown)
        document.removeEventListener('touchstart', onSwipeStart)
        document.removeEventListener('touchmove', onSwipeMove)
        document.removeEventListener('touchend', onSwipeEnd)
      }
    }
  })

  const scrollCurrentSlide = (scrollAmount) => {
    const slideEl = currentSlideRef.current
    if (slideEl && scrollAmount) {
      slideEl.scrollTop += scrollAmount
    }
  }

  // Swipe handling methods

  const onSwipeLeft = _.debounce(props.onSwipeLeft, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)
  const onSwipeRight = _.debounce(props.onSwipeRight, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)

  const onSwipeStart = (event) => {
    const currentSlideEl = currentSlideRef.current
    if (!currentSlideEl || isSwiping) {
      return
    }
    const { width } = currentSlideEl.getBoundingClientRect()

    stopBumpAnimation()
    setSwipe({
      touchStartX: _.get(event, 'touches[0].pageX'),
      touchStartY: _.get(event, 'touches[0].pageY'),
      maxWidth: width * MAX_SWIPE_WIDTH_FACTOR
    })
    setSwiping(true)
    setSwipeDirection(SwipeDirectionEnum.NONE)
    setSwipeWidth(0)
  }

  const onSwipeMove = (event) => {
    if (!isSwiping) {
      return
    }
    // Determine the delta of the movement
    const moveX = _.get(event, 'touches[0].pageX')
    const moveY = _.get(event, 'touches[0].pageY')
    const diffX = swipe.touchStartX - moveX
    const diffY = swipe.touchStartY - moveY
    // Cancel the swipe if there's a more-important gesture
    const isVerticalSwipe = Math.abs(diffY) > Math.abs(diffX)
    const isBrowserBackSwipe = swipe.touchStartX < BROWSER_BACK_SWIPE_RANGE
    if (isVerticalSwipe || isBrowserBackSwipe) {
      setSwipeWidth(0)
      return
    }
    // Determine which slide to swipe in and how far
    let swipeWidth = 0
    if (diffX < 0) {
      swipeWidth = Math.min(-diffX, swipe.maxWidth)
    } else if (diffX > 0) {
      swipeWidth = Math.max(-diffX, -swipe.maxWidth)
    }
    // Peek the next/previous slide in by the swipe amount
    setSwipeWidth(swipeWidth)
  }

  const onSwipeEnd = () => {
    if (!isSwiping) {
      return
    }
    const threshold = swipe.maxWidth * MIN_SWIPE_WIDTH_FACTOR
    // Clear swipe state
    setSwipe(null)
    setSwiping(false)
    setSwipeWidth(0)

    // Determine if swipe triggered index update
    if (Math.abs(swipeWidth) > threshold) {
      if (swipeWidth > 0) {
        onSwipeLeft()
      }
      if (swipeWidth < 0) {
        onSwipeRight()
      }
    }
  }

  // Other event handling methods

  const onKeyDown = (event) => {
    const { keyCode, target: { tagName } } = event
    // Ignore keyCodes that the modal is not interested in
    if (!captureKeyCodes.includes(keyCode)) {
      return
    }
    // Ensure text-capturing inputs keep doing their thing
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
      return
    }
    event.preventDefault()

    stopBumpAnimation()
    switch (keyCode) {
      case CLOSE_MODAL_KEYCODE:
        stopBumpAnimation()
        props.onClose()
        break
      case SWIPE_LEFT_KEYCODE:
        stopBumpAnimation()
        onSwipeLeft()
        break
      case SWIPE_RIGHT_KEYCODE:
        stopBumpAnimation()
        onSwipeRight()
        break
      case SCROLL_UP_KEYCODE:
        scrollCurrentSlide(-KEY_SCROLL_AMOUNT)
        break
      case SCROLL_DOWN_KEYCODE:
        scrollCurrentSlide(KEY_SCROLL_AMOUNT)
        break
    }
  }

  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag(ArticleProfileModalBackdrop, props),
      tag(ArticleProfileModalContent, {
        ...props,
        swipeWidth,
        isSwiping,
        swipeDirection,
        currentSlideRef,
        onSwipeLeft,
        onSwipeRight
      }),
      tag(ArticleProfileModalCloseButton, props),
      tag(ArticleProfileModalSwipeButtons, props)
    )
  )
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')

  ArticleProfileModal.propTypes = {
    // Values
    className: PropTypes.string,
    currentIndex: PropTypes.number.isRequired,
    modalDepth: PropTypes.number.isRequired,
    slideCount: PropTypes.number.isRequired,
    // Callbacks
    onClose: PropTypes.func.isRequired,
    onSlideRefresh: PropTypes.func.isRequired,
    onSwipeLeft: PropTypes.func.isRequired,
    onSwipeRight: PropTypes.func.isRequired
  }
}

module.exports = ArticleProfileModal

// Bump animation hook

const bumpAnimationKeyframes = (() => {
  const initialDelay = 2000
  const duration = 500
  const bumpOffset = -50
  return _.range(0, 4).map(i => ({
    delay: initialDelay + (i * duration),
    offset: i % 2 ? 0 : bumpOffset
  }))
})()

function useBumpAnimation({ isNewlyVisible, setOffset }) {
  const [ isPlaying, setPlaying ] = React.useState(false)
  const [ timerIds, setTimerIds ] = React.useState([])

  const stopBumpAnimation = () => {
    if (!isPlaying) {
      return
    }
    timerIds.forEach(clearTimeout)
    setPlaying(false)
    setOffset(0)
  }

  const startBumpAnimation = () => {
    const newTimerIds = bumpAnimationKeyframes.map(keyframe => {
      return setTimeout(() => {
        setOffset(keyframe.offset)
      }, keyframe.delay)
    })
    setTimerIds(newTimerIds)
    setPlaying(true)
  }

  React.useEffect(() => {
    if (mediaQueryManager && mediaQueryManager.matches('small-and-down')) {
      if (isNewlyVisible && !isPlaying) {
        startBumpAnimation()
      }
    }
  })

  return stopBumpAnimation
}

// BEM Block entity Element components

function ArticleProfileModalBackdrop(props) {
  return (
    tag('div', {
      className: bem.element('backdrop'),
      onClick: props.onClose
    })
  )
}

function ArticleProfileModalCloseButton(props) {
  return (
    tag('div', {
      className: bem.element('close-container')
    },
      tag(CircleButton, {
        className: bem.element('close-button'),
        type: 'button',
        onClick: props.onClose
      })
    )
  )
}

function ArticleProfileModalSwipeButtons(props) {
  return (
    tag('div', {
      className: bem.element('buttons-container')
    },
      tag(ArticleProfileModalSwipeLeftButton, props),
      tag(ArticleProfileModalSwipeRightButton, props)
    )
  )
}

function ArticleProfileModalSwipeLeftButton(props) {
  if (props.currentIndex <= 0) {
    return null
  }
  return (
    tag(Icon, {
      className: bem.element('swipe-button', 'left'),
      id: 'previous',
      type: 'button',
      onClick: props.onSwipeLeft
    })
  )
}

function ArticleProfileModalSwipeRightButton(props) {
  const { currentIndex, slideCount } = props
  if (currentIndex + 1 >= slideCount) {
    return null
  }
  return (
    tag(Icon, {
      className: bem.element('swipe-button', 'right'),
      id: 'next',
      type: 'button',
      onClick: props.onSwipeRight
    })
  )
}

function ArticleProfileModalContent(props) {
  const { swipeWidth, isSwiping, swipeDirection } = props
  const transform = `translateX(${swipeWidth}px)`

  const onCloseCapture = React.useCallback(
    event => {
      const captureClassName = bem.element('slide-outer')
      if (event.target.classList.contains(captureClassName)) {
        event.preventDefault()
        event.stopPropagation()
        props.onClose()
      }
    },
    [ props.onClose ]
  )

  const renderSlide = slideIndexOffset => (
    tag(Slide, {
      ...props,
      // There is no unique key; falling back to index since these don't re-order.
      // Including modal depth so that going deeper forces a re-render.
      key: `${props.modalDepth}:${props.currentIndex + slideIndexOffset}`,
      slideIndexOffset,
      onCloseCapture
    })
  )

  return (
    tag('div', {
      className: bem.element('content', {
        swiping: isSwiping,
        direction: swipeDirection
      }),
      style: { transform }
    },
      // Use an array with keys to better-control component re-use/re-rendering
      [
        renderSlide(-1),
        renderSlide(0),
        renderSlide(+1)
      ]
    )
  )
}

function Slide(props) {
  const { slideIndexOffset } = props
  const slideIndex = props.currentIndex + slideIndexOffset
  const portalContainerRef = React.useRef()
  const scrollableRef = React.useRef()
  const slideOuterRef = composeRefs(
    slideIndexOffset ? null : props.currentSlideRef,
    scrollableRef
  )

  useModalDepthDisableTouchScrollingBehindOverlayEffect(scrollableRef)

  if (slideIndex < 0 || slideIndex >= props.slideCount) {
    portalContainerRef.current = null
    scrollableRef.current = null
    return null
  }

  return (
    tag('div', {
      ref: slideOuterRef,
      // Outer is responsible for the full-window scrolling
      className: bem.element('slide-outer', {
        previous: slideIndexOffset < 0,
        current: slideIndexOffset == 0,
        next: slideIndexOffset > 0
      }),
      onClickCapture: props.onCloseCapture
    },
      tag('div', {
        // Inner is responsible for centered content
        className: bem.element('slide-inner')
      },
        tag('div', {
          ref: portalContainerRef,
          className: bem.element('detail-card-container')
        },
          tag(ArticleProfileDetailCard, {
            articleMixButtonPortalContainer: portalContainerRef,
            slideIndex
          }),
          tag(ArticleProfileRelated, {
            articleMixButtonPortalContainer: portalContainerRef,
            slideIndex
          })
        )
      )
    )
  )
}
